import React from 'react'

import {
  checkDateIsEqual,
  checkIsToday,
  translateDayAbbreviation,
  translateMonthAbbreviation,
  translateMonthName,
} from '../../../CustomCalendar/utils/helpers/date'

import '../../static/css/global.css'
import './Calendar.css'
import { useCalendar } from './hooks/useCalendar'

interface CalendarProps {
  locale?: string
  selectedStartDate: Date
  selectedEndDate: Date
  selectDates: (startDate: Date, endDate: Date, type: 'start' | 'end') => void
  firstWeekDayNumber?: number
  typeDate?: string
}

export const Calendar: React.FC<CalendarProps> = ({
  locale = 'default',
  selectedStartDate,
  selectedEndDate,
  selectDates,
  typeDate,
  firstWeekDayNumber = 2,
}) => {
  const { functions, state } = useCalendar({
    locale,
    firstWeekDayNumber,
    selectedEndDate,
    selectedStartDate,
  })

  return (
    <div className='calendar'>
      <div className='calendar__header'>
        <div
          aria-hidden
          className='calendar__header__arrow__left'
          onClick={() => functions.onClickArrowStart('left')}
        />
        {state.mode === 'days' && (
          <div
            className='calendar__header__title'
            aria-hidden
            onClick={() => functions.setMode('monthes')}
          >
            {translateMonthName(state.monthesNames[state.selectedStartMonth.monthIndex].month)}
            ,&nbsp;
            {state.selectedStartYear || state.selectedEndYear}
          </div>
        )}
        {state.mode === 'monthes' && (
          <div
            className='calendar__header__title'
            aria-hidden
            onClick={() => functions.setMode('years')}
          >
            {state.selectedStartYear || state.selectedEndYear}
          </div>
        )}
        {state.mode === 'years' && (
          <div className='calendar__header__title'>
            {state.selectedStartYearsInterval[0]} -{' '}
            {state.selectedStartYearsInterval[state.selectedStartYearsInterval.length - 1]}
          </div>
        )}
        <div
          aria-hidden
          className='calendar__header__arrow__right'
          onClick={() => functions.onClickArrowStart('right')}
        />
      </div>
      <div className='calendar__body'>
        {state.mode === 'days' && (
          <>
            <div className='calendar__week__names'>
              {state.weekDaysNames.map((weekDaysName) => (
                <div key={weekDaysName.dayShort}>
                  {translateDayAbbreviation(weekDaysName.dayShort)}
                </div>
              ))}
            </div>
            <div className='calendar__days'>
              {state.calendarStartDays.map((day) => {
                const isToday = checkIsToday(day.date)
                const isSelectedStartDay = checkDateIsEqual(day.date, state.selectedStartDay.date)
                const isSelectedEndDay = checkDateIsEqual(day.date, state.selectedEndDay.date)
                const isAdditionalDay = day.monthIndex !== state.selectedStartMonth.monthIndex
                const isDisabled = day.date < new Date()

                return (
                  <div
                    key={`${day.dayNumber}-${day.monthIndex}`}
                    aria-hidden
                    onClick={() => {
                      if (isDisabled) return

                      if (typeDate === 'start') {
                        functions.setSelectedStartDay({
                          ...day,
                          isSelected: true,
                        })
                        selectDates(day.date, null as never, 'start')
                      }

                      if (typeDate === 'end') {
                        if (day.date > state.selectedStartDay.date) {
                          functions.setSelectedEndDay({
                            ...day,
                            isSelected: true,
                          })
                          selectDates(state.selectedStartDay.date, day.date, 'end')
                        }
                      }

                      if (state.selectedStartDay.isSelected && state.selectedEndDay.isSelected) {
                        functions.setSelectedStartDay((prev) => ({
                          ...prev,
                          isSelected: false,
                        }))
                        functions.setSelectedEndDay((prev) => ({
                          ...prev,
                          isSelected: false,
                        }))
                        functions.setSelectedStartDay({
                          ...day,
                          isSelected: true,
                        })
                        selectDates(day.date, null as never, 'start')
                      }

                      // if (state.selectedStartDay.isSelected && state.selectedEndDay.isSelected) {
                      //   functions.setSelectedStartDay((prev) => ({
                      //     ...prev,
                      //     isSelected: false,
                      //   }))
                      //   functions.setSelectedEndDay((prev) => ({
                      //     ...prev,
                      //     isSelected: false,
                      //   }))
                      //   functions.setSelectedStartDay({
                      //     ...day,
                      //     isSelected: true,
                      //   })
                      //   selectDates(day.date, null as never, 'start')
                      // } else if (state.selectedStartDay.isSelected) {
                      //   if (day.date > state.selectedStartDay.date) {
                      //     functions.setSelectedEndDay({
                      //       ...day,
                      //       isSelected: true,
                      //     })
                      //     selectDates(state.selectedStartDay.date, day.date, 'end')
                      //   } else {
                      //     functions.setSelectedStartDay({
                      //       ...day,
                      //       isSelected: true,
                      //     })
                      //     functions.setSelectedEndDay((prev) => ({
                      //       ...prev,
                      //       isSelected: false,
                      //     }))
                      //     selectDates(day.date, null as never, 'start')
                      //   }
                      // } else {
                      //   functions.setSelectedStartDay({
                      //     ...day,
                      //     isSelected: true,
                      //   })
                      //   selectDates(day.date, null as never, 'start')
                      // }
                    }}
                    className={[
                      'calendar__day',
                      isToday ? 'calendar__today__item' : '',
                      isSelectedStartDay && state.selectedStartDay.isSelected && selectedStartDate
                        ? 'calendar__selected__item'
                        : '',
                      isSelectedEndDay && state.selectedEndDay.isSelected && selectedEndDate
                        ? 'calendar__selected__item'
                        : '',
                      state.selectedEndDay &&
                      checkDateIsEqual(day.date, state.selectedEndDay.date) &&
                      state.selectedEndDay.isSelected &&
                      selectedEndDate
                        ? 'calendar__selected__item'
                        : '',
                      isAdditionalDay ? 'calendar__additional__day' : '',
                      state.selectedStartDay &&
                      state.selectedEndDay &&
                      day.date >= state.selectedStartDay.date &&
                      day.date <= state.selectedEndDay.date &&
                      state.selectedStartDay.isSelected &&
                      selectedStartDate
                        ? 'calendar__range__item'
                        : '',
                      !state.selectedEndDay ? 'calendar__not-selected' : '',
                      !state.selectedStartDay ? 'calendar__not-selected' : '',
                      isDisabled ? 'calendar__disabled__day' : '', // Добавляем класс для disabled дней
                    ].join(' ')}
                  >
                    {day.dayNumber}
                  </div>
                )
              })}
            </div>
          </>
        )}

        {state.mode === 'monthes' && (
          <div className='calendar__pick__items__container'>
            {state.monthesNames.map((monthesName) => {
              const isCurrentMonth =
                new Date().getMonth() === monthesName.monthIndex &&
                state.selectedStartYear === new Date().getFullYear()
              const isSelectedMonth = monthesName.monthIndex === state.selectedStartMonth.monthIndex

              return (
                <div
                  key={monthesName.month}
                  aria-hidden
                  onClick={() => {
                    if (!state.selectedStartMonth) {
                      functions.setSelectedStartMonth(monthesName.monthIndex as never)
                      functions.setMode('days')
                    } else {
                      functions.setSelectedEndMonth(monthesName.monthIndex as never)
                      functions.setMode('days')
                    }
                  }}
                  className={[
                    'calendar__pick__item',
                    isSelectedMonth ? 'calendar__selected__item' : '',
                    isCurrentMonth ? 'calendar__today__item' : '',
                  ].join(' ')}
                >
                  {translateMonthAbbreviation(monthesName.monthShort)}
                </div>
              )
            })}
          </div>
        )}

        {state.mode === 'years' && (
          <div className='calendar__pick__items__container'>
            <div className='calendar__unchoosable__year'>
              {state.selectedStartYearsInterval[0] - 1}
            </div>
            {state.selectedStartYearsInterval.map((year) => {
              const isCurrentYear = new Date().getFullYear() === year
              const isSelectedYear =
                year === state.selectedStartYear || year === state.selectedEndYear

              return (
                <div
                  key={year}
                  aria-hidden
                  onClick={() => {
                    if (!state.selectedStartYear) {
                      functions.setSelectedStartYear(year)
                      functions.setMode('monthes')
                    } else {
                      functions.setSelectedEndYear(year)
                      functions.setMode('monthes')
                    }
                  }}
                  className={[
                    'calendar__pick__item',
                    isCurrentYear ? 'calendar__today__item' : '',
                    isSelectedYear ? 'calendar__selected__item' : '',
                  ].join(' ')}
                >
                  {year}
                </div>
              )
            })}
            <div className='calendar__unchoosable__year'>
              {state.selectedStartYearsInterval[state.selectedStartYearsInterval.length - 1] + 1}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
